import React from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import "../styles/modalOrderShipmentHistory.css";

// Icons
import CloseIcon from "./SupportChat/icons/CloseIcon";

// Helpers
import { getCourierServiceTextDescription } from "../helpers/getCourierData";

const ModalOrderShipmentHistory = ({ orderShipmentHistory, onClose }) => {
  const isMobileSize = useSelector((state) => state.isMobileSize);

  if (!orderShipmentHistory) {
    return null;
  }

  const { success, status, courier, history } = orderShipmentHistory;

  const getStatusIcon = (shipmentStatus) => {
    switch (shipmentStatus) {
      case "confirmed":
      case "picked":
      case "delivered":
      case "returned":
        return "✓";
      case "allocated":
      case "on_hold":
        return "●";
      case "picking_up":
      case "return_in_transit":
        return "↗";
      case "dropping_off":
        return "↙";
      case "rejected":
      case "courier_not_found":
      case "cancelled":
      case "disposed":
        return "✕";
      default:
        return "●";
    }
  };

  const getStatusColor = (shipmentStatus) => {
    switch (shipmentStatus) {
      case "confirmed":
      case "picked":
      case "delivered":
      case "returned":
        return "#4CAF50";
      case "allocated":
      case "picking_up":
      case "dropping_off":
      case "return_in_transit":
        return "#FF9800";
      case "on_hold":
        return "#FFC107";
      case "rejected":
      case "courier_not_found":
      case "cancelled":
      case "disposed":
        return "#F44336";
      default:
        return "#9E9E9E";
    }
  };

  const formatStatusLabel = (shipmentStatus) => {
    return shipmentStatus
      .split("_")
      .map((word) => word.toUpperCase())
      .join(" ");
  };

  return (
    <div className="modalOrderShipmentHistoryOverlay" onClick={onClose}>
      <div
        className={`modalOrderShipmentHistoryContainer ${
          isMobileSize ? "modalOrderShipmentHistoryContainerMobile" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <div className="modalOrderShipmentHistoryHeaderContainer">
          <div className="modalOrderShipmentHistoryTitle">Shipment Tracking</div>
          <div
            className="modalOrderShipmentHistoryCloseBtn"
            onClick={onClose}
          >
            <CloseIcon className="close_icon" />
          </div>
        </div>

        {/* Courier Information */}
        {courier && (
          <div className="courierInformationContainer">
            <div className="courierSectionTitle">Courier Information</div>
            <div className="courierDetailsGrid">
              <div className="courierDetailItem">
                <div className="courierDetailLabel">Company</div>
                <div className="courierDetailValue">
                  {courier.company ? courier.company.toUpperCase() : "N/A"}
                </div>
              </div>
              <div className="courierDetailItem">
                <div className="courierDetailLabel">Shipment Status</div>
                <div
                  className="shipmentCurrentStatusBadge"
                  style={{
                    backgroundColor: getStatusColor(status),
                  }}
                >
                  {formatStatusLabel(status)}
                </div>
              </div>
              {(courier.name || courier.phone) && (
                <>
                  <div className="courierDetailItem">
                    <div className="courierDetailLabel">Company Agent Name</div>
                    <div className="courierDetailValue">
                      {courier.name || "N/A"}
                    </div>
                  </div>
                  <div className="courierDetailItem">
                    <div className="courierDetailLabel">Company Agent Contact</div>
                    <div className="courierDetailValue">
                      {courier.phone || "N/A"}
                    </div>
                  </div>
                </>
              )}
              {(courier.driver_name || courier.driver_phone) && (
                <>
                  <div className="courierDetailItem">
                    <div className="courierDetailLabel">Driver Name</div>
                    <div className="courierDetailValue">
                    {courier.driver_name || "N/A"}
                    </div>
                  </div>
                  <div className="courierDetailItem">
                    <div className="courierDetailLabel">Driver Contact</div>
                    <div className="courierDetailValue">
                    {courier.driver_phone || "N/A"}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="shipmentTimelineContainer">
          <div className="timelineTitle">Shipment History</div>
          <div className="timelineHistoryList">
            {history && Array.isArray(history) && history.length > 0 ? (
              history.map((item, index) => {
                const isLastItem = index === history.length - 1;
                const isStatusCompleted =
                  item.status === "confirmed" ||
                  item.status === "picked" ||
                  item.status === "delivered" ||
                  item.status === "returned";
                const isStatusFailed =
                  item.status === "rejected" ||
                  item.status === "courier_not_found" ||
                  item.status === "cancelled" ||
                  item.status === "disposed";

                return (
                  <div
                    key={index}
                    className={`timelineItem ${
                      isStatusCompleted ? "timelineItemCompleted" : ""
                    } ${isStatusFailed ? "timelineItemFailed" : ""}`}
                  >
                    {/* Timeline Dot and Line */}
                    <div className="timelineItemLeft">
                      <div
                        className="timelineDot"
                        style={{
                          backgroundColor: getStatusColor(item.status),
                        }}
                      >
                        <span className="timelineDotIcon">
                          {getStatusIcon(item.status)}
                        </span>
                      </div>
                      {!isLastItem && (
                        <div
                          className="timelineLine"
                          style={{
                            backgroundColor: getStatusColor(item.status),
                          }}
                        ></div>
                      )}
                    </div>

                    {/* Timeline Content */}
                    <div className="timelineItemContent">
                      <div className="timelineItemHeader">
                        <div className="timelineItemStatus">
                          {formatStatusLabel(item.status)}
                        </div>
                        <div className="timelineItemTime">
                          {moment(item.updated_at).format("LLL")}
                        </div>
                      </div>
                      {item.note && (
                        <div className="timelineItemNote">{item.note}</div>
                      )}
                      {item.service_type && (
                        <div className="timelineItemServiceType">
                          Service: {getCourierServiceTextDescription(courier.company, item.service_type)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="timelineEmptyState">
                No shipment history available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalOrderShipmentHistory;
